
import {
  computed, defineComponent, inject, ref, Ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppSearch from '@/components/UI/AppSearch.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppPhotoView from '@/components/AppPhotoView.vue';
import AppFileView from '@/components/AppFileView.vue';

// eslint-disable-next-line import/no-cycle
import { useMessagesStore } from '../store/messagesStore';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '@/modules/drive/store/driveStore';
// eslint-disable-next-line import/no-cycle
import { useSpacesStore } from '../../spaces/store/spacesStore';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { NAVIGATION_PAGES, PHOTO_VIEW_TYPE } from '@/types/enums';
import MessageGroupChatInfo from '../components/MessageGroupChatInfo.vue';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import AppMediaView from '@/components/AppMediaView/AppMediaView.vue';
import DriveMediaView from '@/modules/drive/components/desktop/mediaview/DriveMediaView.vue';
import MessagesConversations from '../components/MessagesConversations.vue';
import DriveAsideMainMenu from '@/modules/drive/components/desktop/DriveAsideMainMenu.vue';
import profile from '@/modules/profile/layouts/Profile.vue';
import DesktopHeader from '@/components/desktop/DesktopHeader.vue';

export default defineComponent({
  computed: {
    profile() {
      return profile;
    },
  },
  components: {
    DesktopHeader,
    AppSearch,
    AppHeader,
    AppPhotoView,
    AppFileView,
    MessageGroupChatInfo,
    AppLazyImage,
    AppMediaView,
    DriveMediaView,
    MessagesConversations,
    DriveAsideMainMenu,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const driveStore = useDriveStore();
    const loaderText = ref<undefined | string>(undefined);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localizeIfPresent = inject('$localizeIfPresent') as Function;

    const isGroupInfo: Ref<boolean> = ref(false);
    const createGroup: Ref<boolean> = ref(false);
    const isAuth = computed(() => !!(mainStore.user.accountId));
    const fullName = computed(() => `${mainStore.user.firstName} ${mainStore.user.lastName}`);

    const photoUrl = computed(() => {
      if (messagesStore.currentConversation?.info.photo) {
        return lowResUrlByViewTypeByUrl(messagesStore.currentConversation.info.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/Group.svg');
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const profilePhoto = computed(() => {
      if (mainStore.user.photo?.url) {
        return lowResUrlByViewTypeByUrl(mainStore.user.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const hideBack = computed(() => route.meta?.hideBackStep || false);

    const currentPage = computed(() => route.meta.navigationPage);
    const showSearch = computed(() => ![NAVIGATION_PAGES.MESSAGES_ID, NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID].includes(currentPage.value!));

    const backText = computed(() => $localize(route.meta.backText));
    const backArrow = computed(() => (route.meta.backArrow));
    const stepBackTo = computed(() => (route.meta.stepBackTo));

    function backAction() {
      if (stepBackTo.value) {
        router.push(stepBackTo.value);
      } else if (router.options.history.state.back === '/messages/my') {
        router.push('/messages/my');
      } else if (router.options.history.state.back === '/') {
        router.push('/messages/my');
      } else {
        router.back();
      }
    }

    function closeFullScreen() {
      driveStore.fullscreenPhoto = undefined;
      driveStore.fullscreenFile = undefined;
      driveStore.appPhotoViewMedia = undefined;
    }

    function navigateTo(rout: string) {
      router.push(rout);
    }

    function conversationNameClicked() {
      if (!messagesStore.currentConversation?.info?.id) {
        return;
      }
      if (messagesStore.currentConversation!.info.isGroup) {
        isGroupInfo.value = true;
      } else {
        router.push(`/profile/${messagesStore.currentConversation!.info.id}`);
      }
    }
    watch(() => route.query.openGroupInfo, () => {
      if (route.query.openGroupInfo) {
        isGroupInfo.value = true;
        loaderText.value = 'Loading settings';
      } else {
        loaderText.value = undefined;
      }
    });

    return {
      hideBack,
      backText,
      loaderText,
      backArrow,
      messagesStore,
      mainStore,
      spacesStore,
      driveStore,
      currentPage,
      showSearch,
      photoUrl,
      isGroupInfo,
      backAction,
      closeFullScreen,
      conversationNameClicked,
      createGroup,
      navigateTo,
      isAuth,
      NAVIGATION_PAGES,
      profilePhoto,
      fullName,
      stepBackTo,
    };
  },
});
